(function () {
  'use strict';

  var MOBILE_QUERY = '(max-width: 767px)';
  var MAX_ANCHORS = 3;
  var RESULTS_PER_ANCHOR = 8;
  var MAX_RESULTS = 4;
  var debounceTimer;
  var requestSequence = 0;

  function isMobile() {
    return window.matchMedia(MOBILE_QUERY).matches;
  }

  function routeRoot() {
    return window.Shopify && window.Shopify.routes && window.Shopify.routes.root
      ? window.Shopify.routes.root
      : '/';
  }

  function cartUrl() {
    return routeRoot() + 'cart.js';
  }

  function normalizeText(value) {
    return String(value || '')
      .toLowerCase()
      .replace(/\b\d+(?:[.,]\d+)?\s?(?:g|kg|ml|l|oz|lb|pack|pk|pcs?|ct)\b/g, ' ')
      .replace(/[^a-z0-9\s]/g, ' ')
      .replace(/\b(?:norwegian|norway|the|and|with|from|for|of|a|an)\b/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function titleSimilarity(a, b) {
    var aTokens = normalizeText(a).split(' ').filter(Boolean);
    var bTokens = normalizeText(b).split(' ').filter(Boolean);
    if (!aTokens.length || !bTokens.length) return 0;

    var aSet = new Set(aTokens);
    var bSet = new Set(bTokens);
    var intersection = 0;
    aSet.forEach(function (token) {
      if (bSet.has(token)) intersection += 1;
    });
    var union = new Set(aTokens.concat(bTokens)).size;
    return union ? intersection / union : 0;
  }

  function normalizedType(product) {
    return normalizeText(product && (product.type || product.product_type || ''));
  }

  function normalizedVendor(product) {
    return normalizeText(product && product.vendor);
  }

  function productImage(product) {
    if (!product) return '';
    if (typeof product.featured_image === 'string') return product.featured_image;
    if (product.featured_image && product.featured_image.url) return product.featured_image.url;
    if (product.featured_image && product.featured_image.src) return product.featured_image.src;
    if (product.images && product.images.length) {
      if (typeof product.images[0] === 'string') return product.images[0];
      return product.images[0].src || product.images[0].url || '';
    }
    return '';
  }

  function imageUrl(url, width) {
    if (!url) return '';
    try {
      var parsed = new URL(url, window.location.origin);
      if (parsed.hostname.indexOf('cdn.shopify.com') !== -1) {
        parsed.searchParams.set('width', String(width));
      }
      return parsed.toString();
    } catch (error) {
      return url;
    }
  }

  function firstAvailableVariant(product) {
    if (!product || !Array.isArray(product.variants)) return null;
    return product.variants.find(function (variant) { return variant.available; }) || product.variants[0] || null;
  }

  function money(cents) {
    var value = Number(cents);
    if (!Number.isFinite(value)) return '';
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      try {
        var format = typeof moneyFormat !== 'undefined' ? moneyFormat : '${{amount}}';
        return window.Shopify.formatMoney(value, format);
      } catch (error) {
        // Fall through to Intl formatting.
      }
    }
    var currency = window.Shopify && window.Shopify.currency && window.Shopify.currency.active
      ? window.Shopify.currency.active
      : 'USD';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency: currency
      }).format(value / 100);
    } catch (error) {
      return (value / 100).toFixed(2) + ' ' + currency;
    }
  }

  function cartSignature(cart) {
    return (cart.items || []).map(function (item) {
      return item.product_id + ':' + item.quantity;
    }).join('|');
  }

  function uniqueAnchors(cart) {
    var seen = new Set();
    var anchors = [];
    (cart.items || []).forEach(function (item) {
      if (!seen.has(item.product_id) && anchors.length < MAX_ANCHORS) {
        seen.add(item.product_id);
        anchors.push(item);
      }
    });
    return anchors;
  }

  function recommendationUrl(productId) {
    return routeRoot() + 'recommendations/products.json?product_id=' + encodeURIComponent(productId) +
      '&limit=' + RESULTS_PER_ANCHOR + '&intent=related';
  }

  function fetchRecommendations(anchor) {
    return fetch(recommendationUrl(anchor.product_id), {
      credentials: 'same-origin',
      headers: { 'Accept': 'application/json' }
    })
      .then(function (response) {
        if (!response.ok) throw new Error('Recommendation request failed');
        return response.json();
      })
      .then(function (payload) {
        return {
          anchor: anchor,
          products: payload && Array.isArray(payload.products) ? payload.products : []
        };
      })
      .catch(function () {
        return { anchor: anchor, products: [] };
      });
  }

  function rankCandidates(cart, batches) {
    var cartProductIds = new Set((cart.items || []).map(function (item) { return Number(item.product_id); }));
    var candidates = new Map();

    batches.forEach(function (batch) {
      batch.products.forEach(function (product, index) {
        var productId = Number(product.id);
        if (!productId || cartProductIds.has(productId)) return;

        var entry = candidates.get(productId);
        if (!entry) {
          entry = {
            product: product,
            score: 0,
            anchors: new Set(),
            bestRank: index,
            nearDuplicate: false
          };
          candidates.set(productId, entry);
        }

        entry.anchors.add(batch.anchor.product_id);
        entry.bestRank = Math.min(entry.bestRank, index);
        entry.score += Math.max(2, 12 - index);

        var type = normalizedType(product);
        var anchorType = normalizeText(batch.anchor.product_type || '');
        if (type && anchorType && type === anchorType) entry.score -= 2.5;

        var vendor = normalizedVendor(product);
        var anchorVendor = normalizeText(batch.anchor.vendor || '');
        if (vendor && anchorVendor && vendor === anchorVendor) entry.score -= 1;

        var similarity = titleSimilarity(product.title, batch.anchor.product_title || batch.anchor.title);
        if (similarity >= 0.68) {
          entry.score -= 8;
          entry.nearDuplicate = true;
        } else if (similarity >= 0.48) {
          entry.score -= 4;
          entry.nearDuplicate = true;
        }

        var anchorPrice = Number(batch.anchor.final_price || batch.anchor.price || 0);
        var candidatePrice = Number(product.price || 0);
        if (anchorPrice > 0 && candidatePrice > 0) {
          if (candidatePrice <= anchorPrice) entry.score += 1;
          if (candidatePrice > anchorPrice * 1.75) entry.score -= 1.5;
        }
      });
    });

    candidates.forEach(function (entry) {
      if (entry.anchors.size > 1) entry.score += (entry.anchors.size - 1) * 5;
    });

    var ranked = Array.from(candidates.values()).sort(function (a, b) {
      if (b.score !== a.score) return b.score - a.score;
      return a.bestRank - b.bestRank;
    });

    var selected = [];
    var typeCounts = new Map();
    var vendorCounts = new Map();
    var nearDuplicateCount = 0;

    ranked.forEach(function (entry) {
      if (selected.length >= MAX_RESULTS) return;
      var product = entry.product;
      var type = normalizedType(product);
      var vendor = normalizedVendor(product);
      var typeCount = type ? (typeCounts.get(type) || 0) : 0;
      var vendorCount = vendor ? (vendorCounts.get(vendor) || 0) : 0;

      if (type && typeCount >= 2) return;
      if (vendor && vendorCount >= 2) return;
      if (entry.nearDuplicate && nearDuplicateCount >= 1) return;

      selected.push(entry);
      if (type) typeCounts.set(type, typeCount + 1);
      if (vendor) vendorCounts.set(vendor, vendorCount + 1);
      if (entry.nearDuplicate) nearDuplicateCount += 1;
    });

    if (selected.length < Math.min(3, MAX_RESULTS)) {
      ranked.forEach(function (entry) {
        if (selected.length >= MAX_RESULTS) return;
        if (selected.indexOf(entry) === -1) selected.push(entry);
      });
    }

    return selected.slice(0, MAX_RESULTS).map(function (entry) { return entry.product; });
  }

  function safeText(value) {
    var node = document.createElement('span');
    node.textContent = String(value || '');
    return node.innerHTML;
  }

  function productCard(product) {
    var variant = firstAvailableVariant(product);
    if (!variant) return '';

    var directAdd = Array.isArray(product.variants) && product.variants.length === 1 && variant.available;
    var url = product.url || (product.handle ? routeRoot() + 'products/' + product.handle : '#');
    var image = imageUrl(productImage(product), 320);
    var title = safeText(product.title);
    var price = money(product.price);
    var productId = Number(product.id);

    var action = directAdd
      ? '<form method="post" action="' + routeRoot() + 'cart/add" class="norbites-cart-upsell__form">' +
          '<input type="hidden" name="id" value="' + safeText(variant.id) + '">' +
          '<input type="hidden" name="quantity" value="1">' +
          '<button type="submit" class="Sd_addProduct norbites-cart-upsell__add" aria-label="Add ' + title + ' to cart">' +
            '<span class="norbites-cart-upsell__add-label">+ Add</span>' +
            '<span class="norbites-cart-upsell__spinner" aria-hidden="true"></span>' +
          '</button>' +
        '</form>'
      : '<a class="norbites-cart-upsell__choose" href="' + safeText(url) + '">Choose</a>';

    return '<article class="norbites-cart-upsell__card" data-product-id="' + productId + '">' +
      '<a class="norbites-cart-upsell__image-link" href="' + safeText(url) + '" aria-label="' + title + '">' +
        (image ? '<img class="norbites-cart-upsell__image" src="' + safeText(image) + '" alt="" loading="lazy" width="160" height="160">' : '') +
      '</a>' +
      '<div class="norbites-cart-upsell__content">' +
        '<a class="norbites-cart-upsell__title" href="' + safeText(url) + '">' + title + '</a>' +
        '<div class="norbites-cart-upsell__price">' + safeText(price) + '</div>' +
        action +
      '</div>' +
    '</article>';
  }

  function removeSurface() {
    document.querySelectorAll('[data-norbites-cart-upsell]').forEach(function (node) { node.remove(); });
  }

  function renderSurface(cart, products) {
    var drawerBody = document.querySelector('[data-drawer-body]');
    if (!drawerBody || !isMobile()) return;
    var cartItems = drawerBody.querySelector('.cart-item-container');
    var itemsWrapper = cartItems ? cartItems.querySelector('.cart-items-wrapper') : null;
    if (!cartItems || !itemsWrapper) return;

    removeSurface();
    if (!products.length) return;

    var section = document.createElement('section');
    section.className = 'norbites-mobile-cart-upsell';
    section.setAttribute('data-norbites-cart-upsell', '');
    section.setAttribute('data-cart-signature', cartSignature(cart));
    section.setAttribute('aria-label', 'Recommended products');
    section.innerHTML =
      '<div class="norbites-cart-upsell__heading-row">' +
        '<h3 class="norbites-cart-upsell__heading">Complete your basket</h3>' +
        '<span class="norbites-cart-upsell__hint" aria-hidden="true">Swipe →</span>' +
      '</div>' +
      '<div class="norbites-cart-upsell__track">' + products.map(productCard).join('') + '</div>';

    itemsWrapper.insertAdjacentElement('afterend', section);
  }

  function loadForCurrentCart() {
    if (!isMobile()) {
      removeSurface();
      return;
    }

    var drawerBody = document.querySelector('[data-drawer-body]');
    if (!drawerBody || !drawerBody.querySelector('.cart-item-container')) return;

    var sequence = ++requestSequence;
    fetch(cartUrl(), { credentials: 'same-origin', headers: { 'Accept': 'application/json' } })
      .then(function (response) {
        if (!response.ok) throw new Error('Cart request failed');
        return response.json();
      })
      .then(function (cart) {
        if (sequence !== requestSequence || !cart || !cart.item_count) {
          removeSurface();
          return null;
        }

        var signature = cartSignature(cart);
        var existing = document.querySelector('[data-norbites-cart-upsell][data-cart-signature="' + signature.replace(/"/g, '\\"') + '"]');
        if (existing) return null;

        var anchors = uniqueAnchors(cart);
        if (!anchors.length) return null;
        return Promise.all(anchors.map(fetchRecommendations)).then(function (batches) {
          if (sequence !== requestSequence) return null;
          return { cart: cart, products: rankCandidates(cart, batches) };
        });
      })
      .then(function (result) {
        if (!result || sequence !== requestSequence) return;
        renderSurface(result.cart, result.products);
      })
      .catch(function () {
        // Recommendations are optional. Keep the cart usable if Shopify's endpoint fails.
      });
  }

  function scheduleLoad() {
    window.clearTimeout(debounceTimer);
    debounceTimer = window.setTimeout(loadForCurrentCart, 90);
  }

  function observeDrawer() {
    var drawerBody = document.querySelector('[data-drawer-body]');
    if (!drawerBody) return false;

    var observer = new MutationObserver(function (mutations) {
      var meaningfulChange = mutations.some(function (mutation) {
        return Array.prototype.some.call(mutation.addedNodes, function (node) {
          return node.nodeType === 1 && !node.matches('[data-norbites-cart-upsell]') && !node.closest('[data-norbites-cart-upsell]');
        });
      });
      if (meaningfulChange) scheduleLoad();
    });

    observer.observe(drawerBody, { childList: true, subtree: true });
    scheduleLoad();
    return true;
  }

  function init() {
    if (!observeDrawer()) {
      var bodyObserver = new MutationObserver(function () {
        if (observeDrawer()) bodyObserver.disconnect();
      });
      bodyObserver.observe(document.body, { childList: true, subtree: true });
    }

    var mediaQuery = window.matchMedia(MOBILE_QUERY);
    var handleMediaChange = function () {
      if (isMobile()) scheduleLoad();
      else removeSurface();
    };
    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', handleMediaChange);
    } else if (typeof mediaQuery.addListener === 'function') {
      mediaQuery.addListener(handleMediaChange);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
